
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, Info, AlertCircle, CheckCircle, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useNotifications, NotificationType } from "@/lib/context/notification-context";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export const NotificationDropdown = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead, refreshNotifications } = useNotifications();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Add effect to auto-close popover after navigation
  useEffect(() => {
    return () => setOpen(false);
  }, []);

  const handleNotificationClick = (notification: any, e: React.MouseEvent) => {
    // Stop propagation to prevent the popover from intercepting the click
    e.stopPropagation();
    e.preventDefault();
    
    markAsRead(notification.id);
    if (notification.link) {
      // Close the popover before navigation
      setOpen(false);
      setTimeout(() => {
        navigate(notification.link!);
      }, 10);
    }
  };

  const handleRefresh = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setLoading(true);
    try {
      await refreshNotifications();
    } catch (error) {
      console.error("Error refreshing notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    markAllAsRead();
  };

  const getIcon = (type: NotificationType) => {
    switch (type) {
      case "info":
        return <Info className="h-4 w-4 text-blue-500" />;
      case "success":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "warning":
        return <AlertCircle className="h-4 w-4 text-amber-500" />;
      case "error":
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Info className="h-4 w-4" />;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="relative rounded-full hover:bg-accent/50 transition-all">
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] text-white animate-pulse">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Notifications</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0 bg-white dark:bg-gray-950" align="end">
        <div className="flex items-center justify-between px-4 py-2 border-b">
          <h3 className="font-medium">Notifications</h3>
          <div className="flex items-center gap-2">
            {loading ? (
              <Button variant="ghost" size="sm" className="h-8 text-xs" disabled>
                <RotateCw className="h-3.5 w-3.5 mr-1 animate-spin" />
                Refreshing...
              </Button>
            ) : (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 text-xs" 
                onClick={handleRefresh}
              >
                <RotateCw className="h-3.5 w-3.5 mr-1" />
                Refresh
              </Button>
            )}
            
            {unreadCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 text-xs" 
                onClick={handleMarkAllAsRead}
              >
                <Check className="h-3.5 w-3.5 mr-1" />
                Mark all as read
              </Button>
            )}
          </div>
        </div>
        
        <ScrollArea className="h-[350px]">
          {notifications.length > 0 ? (
            <div className="py-1">
              {notifications.map((notification) => (
                <Button
                  key={notification.id}
                  variant="ghost"
                  className={cn(
                    "w-full flex justify-start gap-3 px-4 py-3 h-auto rounded-none hover:bg-accent/50 border-b border-border/50 transition-colors",
                    !notification.read && "bg-accent/10"
                  )}
                  onClick={(e) => handleNotificationClick(notification, e)}
                >
                  <div className="mt-1 flex-shrink-0">{getIcon(notification.type)}</div>
                  <div className="flex-1 space-y-1 text-left overflow-hidden">
                    <div className="flex items-center justify-between">
                      <p className={cn("text-sm font-medium", !notification.read && "text-primary")}>{notification.title}</p>
                      {!notification.read && (
                        <Badge variant="secondary" className="h-1.5 w-1.5 rounded-full flex-shrink-0 ml-2" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground break-words whitespace-normal line-clamp-2 overflow-ellipsis">{notification.message}</p>
                    <p className="text-[10px] text-muted-foreground/60">
                      {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                    </p>
                  </div>
                </Button>
              ))}
            </div>
          ) : (
            <div className="px-4 py-8">
              <Alert>
                <AlertDescription className="text-center text-muted-foreground">
                  {loading ? "Loading notifications..." : "No notifications yet"}
                </AlertDescription>
              </Alert>
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
